/** Thin fetch wrapper: base path, bearer token, dev-mode actor header, typed errors. */
import { envOr } from '@/lib/env';

const BASE = envOr(import.meta.env.VITE_API_BASE, '/api');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuth() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isMissing() {
    return this.status === 404;
  }
}

/**
 * Raised INSTEAD of sending a request that would have no credential on it.
 *
 * Under Entra the app boots before MSAL has a token: msal-react seeds its `accounts` from the
 * session cache synchronously, so the shell can mount — and fire queries — while
 * handleRedirectPromise is still running. Sending those requests anyway produced real server
 * 401s that TanStack Query caches and never retries, which flipped the shell to the sign-in
 * screen and looped. Failing locally keeps the cache clean and the error retryable.
 */
export class AuthNotReadyError extends Error {
  constructor(readonly path: string) {
    super('Not signed in yet — the request was not sent.');
    this.name = 'AuthNotReadyError';
  }
}

type TokenProvider = () => Promise<string | null> | string | null;

let tokenProvider: TokenProvider = () => null;
/** AUTH_MODE=dev: the seeded user the API should act as when no bearer token exists. */
let devActor: string | null = null;
/**
 * How this build authenticates. `dev` means the API carries a session cookie, so a request with
 * no headers is still authenticated and nothing is gated. `entra` means every request needs a
 * bearer token, and one that lacks it is a bug, not a request.
 */
let authMode: 'entra' | 'dev' = 'dev';

export function setAuthMode(mode: 'entra' | 'dev') {
  authMode = mode;
}

export function setTokenProvider(fn: TokenProvider) {
  tokenProvider = fn;
}

export function setDevActor(userKey: string | null) {
  devActor = userKey;
}

export function getDevActor() {
  return devActor;
}

/**
 * Auth headers for a request this module does not make itself — multipart uploads, EventSource
 * polyfills, anything that cannot go through apiFetch. In AUTH_MODE=dev the session is a
 * cookie, so this is empty and the request still authenticates; under Entra it carries the
 * bearer token. Feature lanes should use this rather than reading tokens themselves.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const token = await tokenProvider();
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (devActor) headers['x-cigs-dev-user'] = devActor;
  return headers;
}

/**
 * Fail closed: in Entra mode a request without a bearer token is never sent. In dev mode the
 * cookie does the work, so nothing is gated and behaviour is exactly as before.
 */
async function credentialedHeaders(path: string): Promise<Record<string, string>> {
  const headers = await getAuthHeaders();
  if (authMode === 'entra' && !headers.Authorization) throw new AuthNotReadyError(path);
  return headers;
}

/** POST a FormData body (document upload) with auth attached and errors shaped like apiFetch. */
export async function apiUpload<T>(path: string, form: FormData, opts: { correlationId?: string } = {}): Promise<T> {
  const headers = await credentialedHeaders(path);
  if (opts.correlationId) headers['x-correlation-id'] = opts.correlationId;
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form });
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = text;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      /* keep the raw text */
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, parsed);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** correlation id groups the writes of one user action in change_log */
  correlationId?: string;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await credentialedHeaders(path)),
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.correlationId) headers['x-correlation-id'] = opts.correlationId;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = text;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      /* keep the raw text */
    }
    const message =
      (parsed && typeof parsed === 'object' && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : null) ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, message, parsed);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Downloads a file produced by the API (CSV / XLSX exports) with auth headers attached. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { headers: await credentialedHeaders(path) });
  if (!res.ok) throw new ApiError(res.status, `Export failed (${res.status})`);
  const blob = await res.blob();
  triggerDownload(blob, filename);
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function newCorrelationId(): string {
  return crypto.randomUUID();
}
